import axios from "axios";
import { useRef, useState, useEffect } from "react";
import {
  FormControl,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  List,
  Alert,
} from "@mui/material";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import { getEvidenceType } from "../../../common/EntitiesManagement";
import localize from "../../../common/localize";
import Combobox from "../../../components/common/Combobox";
import {
  EvidenceTypeEnum,
  EvidenceUploadStatusEnum,
  getEvidenceTypeExtension,
  getFileMaxSize,
} from "../../../common/utils";
import { FileUploader } from "react-drag-drop-files";
import EvidenceItem from "./EvidenceItem";
import useGlobalContext from "../../../hooks/useGlobalContext";

export default function AddOrEditEvidence({
  evidenceModel,
  setEvidenceModel,
  updateFile,
  setUpdateFile,
  selectedFiles,
  setSelectedFiles,
  isUploading,
}) {
  const { EVENTS_API, config } = useGlobalContext();
  const availableFileTypes = ["jpeg", "jpg", "png", "mp3", "mp4", "pdf"];
  const inputFile = useRef(null);
  const [fileName, setFileName] = useState(null);
  const [fileExtension, setFileExtension] = useState(
    getEvidenceTypeExtension(evidenceModel?.EvidenceType),
  );
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(undefined);

  useEffect(() => {
    getSections();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      setEvidenceModel({
        ...evidenceModel,
        SectionId: selectedSection.Id,
      });

      if (selectedFiles && selectedFiles.length > 0) {
        {
          setSelectedFiles(
            selectedFiles.map((file) => {
              return {
                ...file,
                SectionId: selectedSection.Id,
              };
            }),
          );
        }
      }
      return;
    }

    setEvidenceModel({
      ...evidenceModel,
      SectionId: 0,
    });

    if (selectedFiles && selectedFiles.length > 0) {
      {
        setSelectedFiles(
          selectedFiles.map((file) => {
            return {
              ...file,
              SectionId: 0,
            };
          }),
        );
      }
    }
  }, [selectedSection]);

  const getSections = async () => {
    const data = await axios.get(
      `${EVENTS_API}/${evidenceModel?.eventId}/evidence-section`,
      config,
    );
    if (data?.data?.length > 0) {
      setSections([...data.data]);
    }
  };

  const removeExtension = (fileNameWithExtension) => {
    const splitted = fileNameWithExtension.split(".");
    if (splitted.length <= 2) {
      return splitted[0];
    }

    splitted.pop();
    return splitted.join(" ");
  };

  useEffect(() => {
    if (
      fileName &&
      setEvidenceModel &&
      (!evidenceModel.Name || evidenceModel.Name.replace(" ", "").length === 0)
    ) {
      setEvidenceModel({
        ...evidenceModel,
        Name: removeExtension(fileName),
      });
    }
  }, [fileName]);

  const handleCheckedChanged = (e) => {
    setUpdateFile(e.target.checked);
  };

  const handleChange = (e) => {
    setEvidenceModel({
      ...evidenceModel,
      Name: e.target.value,
    });
  };

  const handleEvidenceTypeChanged = (e) => {
    if (!e) {
      setEvidenceModel({
        ...evidenceModel,
        EvidenceType: EvidenceTypeEnum.Image,
        EvidenceTypeId: EvidenceTypeEnum.Image,
      });
    } else {
      setEvidenceModel({
        ...evidenceModel,
        EvidenceType: e.Id,
        EvidenceTypeId: e.Id,
      });
    }

    setFileExtension(getEvidenceTypeExtension(e.Id));
  };

  const onButtonClick = () => {
    // `current` points to the mounted file input element
    inputFile.current.click();
  };

  const changeFilePath = (e) => {
    if (e.target.files) {
      setEvidenceModel({
        ...evidenceModel,
        File: e.target.files[0],
        Path: e.target.files[0].name,
      });

      setFileName(e.target.files[0].name);
    }
  };

  const getEvidenceTypeId = (evidence) => {
    if (evidence) {
      const split_file_name = evidence.name.split(".");

      switch (split_file_name[split_file_name.length - 1].toLowerCase()) {
        case "jpg":
        case "jpeg":
        case "png":
          return EvidenceTypeEnum.Image;
        case "mp3":
          return EvidenceTypeEnum.Audio;
        case "mp4":
          return EvidenceTypeEnum.Video;
        case "pdf":
          return EvidenceTypeEnum.Document;
      }
    }

    return 0; // No data
  };

  const getEvidenceData = (file) => {
    const typeID = getEvidenceTypeId(file);

    let isFileSizeExceeded = file.size > getFileMaxSize();

    return {
      eventId: evidenceModel.eventId,
      CreatedBy: evidenceModel.CreatedBy,
      Status: isFileSizeExceeded ? EvidenceUploadStatusEnum.Failed : 0,
      Error: isFileSizeExceeded ? localize.error_max_file_size : undefined,
      Name: file.name,
      File: file,
      Path: file.name,
      EvidenceTypeId: typeID,
      EvidenceType: {
        Id: typeID,
        Name: getEvidenceType(typeID),
      },
      GpsLongidute: 0,
      GpsLatitude: 0,
      Available: 1,
      FromWeb: true,
      SectionId: selectedSection?.Id ?? 0,
    };
  };

  const handleFilesDragDrop = (data) => {
    length = data.length;

    if (length > 0) {
      setSelectedFiles(Array.from(data).map((f) => getEvidenceData(f)));
      return;
    }

    setSelectedFiles([getEvidenceData(data)]);
  };

  const showSingleEvidenceComponent = () => {
    return (
      <div className="flex-col flex gap-3 mt-2 w-full">
        {sections?.length > 0 && (
          <Combobox
            id="sections"
            fieldLabel={localize.section}
            fieldOptions={sections ?? 0}
            fieldValue={selectedSection}
            handleValueChange={(value) => setSelectedSection(value)}
          />
        )}
        <Combobox
          isRequired
          disabled={evidenceModel?.Id > 0}
          id="EvidenceType"
          handleValueChange={handleEvidenceTypeChanged}
          fieldLabel={localize.evidence_type}
          fieldOptions={[
            {
              Id: EvidenceTypeEnum.Image,
              Name: getEvidenceType(EvidenceTypeEnum.Image),
            },
            {
              Id: EvidenceTypeEnum.Audio,
              Name: getEvidenceType(EvidenceTypeEnum.Audio),
            },
            {
              Id: EvidenceTypeEnum.Video,
              Name: getEvidenceType(EvidenceTypeEnum.Video),
            },
            {
              Id: EvidenceTypeEnum.Document,
              Name: getEvidenceType(EvidenceTypeEnum.Document),
            },
          ]}
          fieldValue={{
            Id: evidenceModel?.EvidenceType,
            Name: getEvidenceType(evidenceModel?.EvidenceType),
          }}
        />

        <div className="w-full">
          <FormControl required fullWidth>
            <TextField
              required
              InputLabelProps={getInputLabelProps()}
              inputProps={getInputProps()}
              size="small"
              fullWidth
              id="outlined-basic"
              name="Name"
              label={localize.name}
              variant="outlined"
              onChange={handleChange}
              value={evidenceModel?.Name}
            />
          </FormControl>
        </div>

        <FormControlLabel
          disabled={evidenceModel?.Id === 0}
          className="my-2"
          size="small"
          control={
            <Checkbox
              size="small"
              checked={updateFile}
              onChange={handleCheckedChanged}
            />
          }
          label={
            <span style={{ fontSize: "0.8em" }}>{localize.attach_file}</span>
          }
        />
        <div>
          <label className="page_description">{`${localize.warning_evidence_input_file}: ${fileExtension}`}</label>
        </div>
        <input
          type="file"
          id="file"
          accept={fileExtension}
          ref={inputFile}
          onChange={changeFilePath}
          style={{ display: "none" }}
        />
        <Button
          disabled={!updateFile}
          className="w-full"
          color="primary"
          variant="outlined"
          onClick={() => {
            onButtonClick();
          }}
        >
          {localize.select_file}
        </Button>
        {fileName && (
          <label className="page_description">{`${localize.file}: ${fileName}`}</label>
        )}

        {evidenceModel.Id === 0 && showFileUploader()}
      </div>
    );
  };

  const showFileUploader = () => {
    return (
      <Box className="w-full flex">
        <FileUploader
          classes="w-full"
          name="files"
          types={availableFileTypes}
          label={localize.put_files_here}
          handleChange={handleFilesDragDrop}
          hoverTitle={localize.drop_files_here}
          uploadedLabel={localize.loading}
          multiple
        />
      </Box>
    );
  };

  const deleteEvidence = (index) => {
    selectedFiles.splice(index, 1);
    if (selectedFiles.length > 0) {
      setSelectedFiles([...selectedFiles]);
      return;
    }

    setSelectedFiles([]);
  };

  const showMultipleEvidencesComponent = () => {
    return (
      <Box className="w-full flex flex-col gap-2">
        <Box className="w-full flex gap-2 items-center">
          <Alert className="w-full" severity="info">
            <label className="text-xs">{localize.files_to_upload}</label>
          </Alert>
          {showFileUploader()}
          <Button
            disabled={isUploading}
            size="small"
            color="error"
            variant="text"
            type="button"
            style={{ fontSize: "0.8em" }}
            onClick={() => {
              setSelectedFiles([]);
            }}
            sx={{ width: "200px" }}
          >
            {localize.delete_all}
          </Button>
        </Box>

        {sections?.length > 0 && (
          <Combobox
            id="sections"
            fieldLabel={localize.section}
            fieldOptions={sections ?? 0}
            fieldValue={selectedSection}
            handleValueChange={(value) => setSelectedSection(value)}
          />
        )}

        <List className="w-full flex flex-col gap-2">
          {selectedFiles.map((file, index) => (
            <EvidenceItem
              key={index}
              item={file}
              onDeleteEvidence={() => {
                deleteEvidence(index);
              }}
            />
          ))}
        </List>
      </Box>
    );
  };

  return !selectedFiles || selectedFiles.length <= 0
    ? showSingleEvidenceComponent()
    : showMultipleEvidencesComponent();
}
